import { FaIcon } from '@components/FaIcon';
import { ComboField } from '@components/Fields/ComboField';
import { DateField } from '@components/Fields/DateField';
import { Fieldset } from '@components/Fields/Fieldset';
import { FloatField } from '@components/Fields/FloatField';
import { IntegerField } from '@components/Fields/IntegerField';
import { StringField } from '@components/Fields/StringField';
import { GridItem } from '@components/GridItem';
import { HorizontalGrid } from '@components/HorizontalGrid';
import { TableHeaderCell } from '@components/Table/TableHeaderCell';
import { VerticalGrid } from '@components/VerticalGrid';
import { IData } from '@hooks/useData';
import { useField } from '@hooks/useField';
import { IForm, useForm } from '@hooks/useForm';
import useFormatter from '@hooks/useFormatter';
import { ILoading } from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { useValidation } from '@hooks/useValidation';
import { useValueState } from '@hooks/useValueState';
import { IconButton, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, makeStyles } from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import ListAltIcon from '@material-ui/icons/ListAlt';
import { ICliente } from '@model/Cliente';
import { IContratoCliente } from '@model/ContratoCliente';
import { EnumEmpresa, IComboEmpresa } from '@model/Empresa';
import { IFaturaItem, faturaItemTipos } from '@model/FaturaItem';
import { IRegistrarFaturaAvulsaRequest, useClienteService } from '@services/ClienteService';
import { useThemeColor } from '@themes/Colors';
import { yup } from '@validations/Yup';
import clsx from 'clsx';
import React from 'react';

export type FaturaRegistrarFormularioProps = {
    cliente: ICliente
    form: IForm<Partial<IRegistrarFaturaAvulsaRequest>>
    data: IData<IFaturaItem>
    loading: ILoading
    empresaCombo: IComboEmpresa
}

export function FaturaRegistrarFormulario ({ cliente, form, data, loading, empresaCombo }: FaturaRegistrarFormularioProps) {
    const { fields } = form;

    const tipoInputRef = React.createRef<HTMLInputElement>();
    const descricaoInputRef = React.createRef<HTMLInputElement>();
    const contratoClienteIdInputRef = React.createRef<HTMLInputElement>();
    const cls = useStyles();

    const themeColor = useThemeColor();
    const formatter = useFormatter();

    const message = useMessage();

    const contratosFaturaManual = useValueState<IContratoCliente[]>([]);
    const clienteService = useClienteService();

    const empresaIdNotSelected = React.useMemo(() => fields.empresaId.value === undefined || fields.empresaId.value === EnumEmpresa.None, [fields.empresaId.value])

   
    const itensForm = useForm<Partial<IFaturaItem>>({
        tipo: useField(faturaItemTipos[0]),
        descricao: useField(),
        valorTotal: useField(),
        contratoClienteId: useField()
    });

    const validation = useValidation<IFaturaItem>({
        descricao: yup.string().required(),
        valorTotal: yup.number().required().min(0)
    })

    const validationContrato = useValidation<IFaturaItem>({
        contratoClienteId: yup.number().required(),
        valorTotal: yup.number().required().min(0)
    })

    const handleAdd = React.useCallback(() => {
        if (!loading.isLoading) {
            const values = itensForm.getInputValues();

            if (values.tipo === faturaItemTipos[0]) {
                validation.validate(values)
                .then(() => {
                    loading.start();
                    data.addRow(values);
                    itensForm.reset();
                    itensForm.fields.contratoClienteId.setValue(undefined);
                    tipoInputRef.current?.focus();
                    loading.stop();
                })
                .catch(itensForm.setErrorsFromYup)
            }
            else {
                const contrato = contratosFaturaManual.value?.find(x => x.contratoClienteId === values.contratoClienteId);

                values.descricao = contrato?.linhaContratoOperadoraEmpresaNome === "Astel" || contrato?.linhaContratoOperadoraEmpresaNome === "ASUS"
                    ? "Mensalidade do Associado"
                    : `Valor do contrato nº ${contrato?.contratoClienteId} da linha ${formatter.toCelular(contrato?.linhaNumero)}`;

                validationContrato.validate(values)
                .then(() => {
                    loading.start();
                    data.addRow(values);
                    itensForm.reset();
                    contratoClienteIdInputRef.current?.focus();
                    loading.stop();
                })
                .catch(itensForm.setErrorsFromYup)
            }

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, descricaoInputRef, loading, validation]);

    const listarContratosFaturaManual = React.useCallback(async (empresaId: EnumEmpresa) => {
        if (!loading.isLoading) {
            loading.start();
                clienteService.listarContratosFaturaManual(cliente.clienteId, empresaId)
                    .then(contratosFaturaManual.handleResponse)
                    .catch(message.showException)
                    .finally(loading.stop);
        }
    }, [cliente.clienteId, clienteService, contratosFaturaManual.handleResponse, loading, message.showException]);

    const getValorColor = React.useCallback((valor: number) => {
        return valor >= 0 ? themeColor.status.verde : themeColor.status.vermelho
    }, [themeColor.status.verde, themeColor.status.vermelho]);

    React.useEffect(() => {
        listarContratosFaturaManual(fields.empresaId.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fields.empresaId]);

    React.useEffect(() => {
        if (itensForm.fields.contratoClienteId.value !== undefined) {
            const contrato = contratosFaturaManual.value?.find(x => x.contratoClienteId === itensForm.fields.contratoClienteId.value);
            itensForm.fields.valorTotal.setValue(contrato?.valorPlano);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contratosFaturaManual.value, itensForm.fields.contratoClienteId.value]);

    return (
        <VerticalGrid>
            <Fieldset
                title="Dados da Fatura"
                icon={<FaIcon icon="fa-solid fa-file-invoice-dollar" />}
                hasMarginTop={false}
            >
                <HorizontalGrid spacing={1}>
                    <GridItem xs={4}>
                        <ComboField 
                            disabled={data.items.filter(x => x.contratoClienteId !== undefined).length > 0}
                            field={fields.empresaId}
                            label="Empresa"
                            updateFieldStateOnChange
                            required
                        >
                            {empresaCombo.createOptions()}
                        </ComboField>
                    </GridItem>

                    <GridItem xs={4}>
                        <DateField 
                            field={fields.dataVencimento}
                            label="Vencimento"
                            required
                        />
                    </GridItem>

                    <GridItem xs={4}>
                        <IntegerField 
                            field={fields.quantidade}
                            label="Nº Faturas"
                            required
                        />
                    </GridItem>

                    <GridItem xs={12}>
                        <StringField 
                            field={fields.descricaoRemessa}
                            label="Descrição do Arquivo de Remessa"
                            required
                        />
                    </GridItem>
                </HorizontalGrid>
            </Fieldset>

            <Fieldset
                title="Itens da Fatura"
                icon={<ListAltIcon />}
                hasContentPadding={false}
            >
                <GridItem xs={12} className={clsx(themeColor.component.gridFilterBar, cls.fieldsetAddBox)}>
                    <HorizontalGrid spacing={1}>
                        <GridItem xs={2}>
                            <ComboField
                                disabled={empresaIdNotSelected}
                                inputRef={tipoInputRef}
                                field={itensForm.fields.tipo}
                                label="Tipo"
                                updateFieldStateOnChange
                                required
                                InputLabelProps={{
                                    shrink: true
                                }}
                            >
                                <MenuItem value={faturaItemTipos[0]}>Livre</MenuItem>
                                <MenuItem value={faturaItemTipos[3]}>Vinculado</MenuItem>
                            </ComboField>

                        </GridItem>

                        {
                            itensForm.fields.tipo.value !== faturaItemTipos[3] && (
                                <GridItem xs={7}>
                                    <StringField 
                                        inputRef={descricaoInputRef}
                                        disabled={empresaIdNotSelected}
                                        field={itensForm.fields.descricao}
                                        label="Descrição"
                                        required
                                        InputLabelProps={{
                                            shrink: true
                                        }}
                                    />
                                </GridItem>
                            )
                        }

                        {
                            itensForm.fields.tipo.value === faturaItemTipos[3] && (
                                <GridItem xs={7}>
                                    <ComboField 
                                        inputRef={contratoClienteIdInputRef}
                                        disabled={empresaIdNotSelected}
                                        field={itensForm.fields.contratoClienteId}
                                        label="Contrato"
                                        required
                                        updateFieldStateOnChange                                        
                                        InputLabelProps={{
                                            shrink: true
                                        }}
                                    >
                                        {
                                            contratosFaturaManual.value?.map(v => (<MenuItem value={v.contratoClienteId}>{`${v.contratoClienteId} - ${formatter.toCelular(v.linhaNumero)}`}</MenuItem>))
                                        }
                                    </ComboField>
                                </GridItem>
                            )
                        }

                        <GridItem xs={2}>
                            <FloatField
                                disabled={empresaIdNotSelected || itensForm.fields.tipo.value !== faturaItemTipos[0]}
                                field={itensForm.fields.valorTotal}
                                label="Valor"
                                updateFieldStateOnChange
                                required
                            />
                        </GridItem>

                        <GridItem xs={1}>
                            <IconButton 
                                disabled={empresaIdNotSelected}
                                onClick={handleAdd} 
                                color="primary"
                            >
                                <AddCircleIcon />
                            </IconButton>
                        </GridItem>
                    </HorizontalGrid>
                </GridItem>

                <GridItem xs={12}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableHeaderCell>Descrição</TableHeaderCell>
                                <TableHeaderCell>ID Contrato</TableHeaderCell>
                                <TableHeaderCell>Valor</TableHeaderCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                        {
                            data.items.map((faturaItem, index) => {
                                return (
                                    <TableRow key={index}>
                                        <TableCell>{faturaItem.descricao}</TableCell>
                                        <TableCell>{faturaItem.contratoClienteId ?? ""}</TableCell>
                                        <TableCell className={getValorColor(faturaItem.valorTotal)}>{formatter.toFloat(faturaItem.valorTotal)}</TableCell>
                                    </TableRow>
                                )
                            })
                        }
                        </TableBody>
                    </Table>
                </GridItem>
            </Fieldset>
        </VerticalGrid>
    )
}

const useStyles = makeStyles(theme => ({
    fieldsetAddBox: {
        padding: theme.spacing(1),
        marginBottom: theme.spacing(1)
    }
}))